"use client"

import React, {useEffect, useState} from 'react';
import {BsQrCodeScan} from "react-icons/bs";
import styles from "./Walk.module.scss";
import {RxExit} from "react-icons/rx";
import useWalkStatus from "@/utils/hooks/useWalkStatus";
import {PiGpsFix} from "react-icons/pi";

import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Fab,
  Stack,
  Step,
  StepButton,
  Stepper,
  Switch,
  Typography
} from "@mui/material";
import {useLocale, useTranslations} from "use-intl";
import ScanSegment from "@/components/ScanSegment/ScanSegment";
import {useAppStore} from "@/store/AppStore";
import {Scan} from "@/types/Scan";
import DoneOutlineIcon from '@mui/icons-material/DoneOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import WalkActions from "@/components/Walk/WalkActions/WalkActions";
import WalkBtns from "@/components/Walk/WalkBtns/WalkBtns";
import {FaHiking} from "react-icons/fa";
import {WalkPath} from "@/types/Walk";
import {abandonPathRequest} from "@/utils/api/Path";
import {MdOutlineMobileScreenShare} from "react-icons/md";
import PlaceIcon from '@mui/icons-material/Place';
import {Link} from "@/i18n/routing";
import LoginAlert from "@/components/LoginAlert/LoginAlert";
import TagScanSegment from '../TagScanSegment/TagScanSegment';

export default function Walk() {
  const locale = useLocale()
  const t = useTranslations('Walk')
  const user = useAppStore((state) => state.user);
  const showAbandon = useAppStore((state) => state.showAbandon);
  const scannedQrCode = useAppStore((state) => state.scannedQrCode)
  const segmentAction = useAppStore((state) => state.segmentAction)
  const action = useAppStore((state) => state.scanningAction)
  const direction = useAppStore((state) => state.scanningDirection)
  const transport = useAppStore((state) => state.scanningTransport)
  const [userDirection, setUserDirection] = useState<"normal" | "inverted" | undefined>(undefined)
  const [userTransport, setUserTransport] = useState<"bicycle" | "foot" | undefined>(undefined)
  const path = useAppStore((state) => state.scanningPath)
  const segments = useAppStore((state) => state.scanningSegments)
  const [currentSegment, setCurrentSegment] = useState<string | undefined>()
  const [currentPath, setCurrentPath] = useState<string | undefined>()
  const steps = ['Scansiona QR Code', 'Scegli Azione', 'Conferma']
  const [activeStep, setActiveStep] = useState(0)
  const results = useAppStore((state) => state.results)
  const errors = useAppStore((state) => state.errors)
  const messages = useAppStore((state) => state.messages)
  const setMessage = useAppStore((state) => state.setMessage)
  const cleanResultsAndErrors = useAppStore((state) => state.cleanResultsAndErrors)
  const setShowAbandon = useAppStore((state) => state.setShowAbandon)
  const [completed] = useState<{ [k: number]: boolean }>({})
  const [showScan, setShowScan] = useState<boolean>(false)
  const [showTagScan, setShowTagScan] = useState<boolean>(false)
  const walkStatus = useWalkStatus()

  useEffect(() => {
    //console.log(errors)
  }, [errors]);

  const handleStep = (step: number) => () => {
    setActiveStep(step);
  };
  const handleNext = () => {
    const newActiveStep = activeStep + 1
    setActiveStep(newActiveStep)
  };
  const handleBack = () => {
    setShowScan(false)
    setShowTagScan(false)
    if (activeStep == 0) return
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleConfirm = async () => {
    if (Array.isArray(scannedQrCode)) {
      if (action == 'start-end') {
        await sendAction('end')
        await sendAction('start')
      }
      await sendAction(action)
      handleNext()
    }
  }

  const sendAction = async (action?: string) => {
    if (Array.isArray(scannedQrCode)) {
      const pid = walkStatus?.paths?.filter((itm) => {
        return itm.current
      }).at(0)?.path_id.toString()
      const currentSeg = walkStatus?.last_user_action?.segment_id

      const filtered = pid && currentSeg ? scannedQrCode.filter((item: Scan) => {
        return String(item.segment_id) === String(currentSeg) && String(item.path_id) === String(pid)
      }) : scannedQrCode

      const actionData = filtered.find((item: Scan) => {
        return item.type === action
      })

      if (!actionData) return
      const finalAction = {
        ...actionData,
        direction: !direction ? String(userDirection) : direction,
        transport: !transport ? String(userTransport) : transport
      }
      if (!user?.access_token || !finalAction) return
      setCurrentSegment(finalAction?.segment_id)
      setCurrentPath(finalAction?.path_id)
      await segmentAction(user.access_token, finalAction, locale)
    }
  }

  const leavePath = async () => {
    const current = walkStatus?.paths?.filter((itm: WalkPath) => {
      return itm.current
    })
    if (current) {
      if (!user?.access_token) return
      const currPath = current.at(0)
      await abandonPathRequest(String(currPath?.path_id), locale, user.access_token)
      cleanResultsAndErrors()
      setShowAbandon(false)
      setActiveStep(0)
      setMessage({
        type: 'success',
        text: t('path_abandoned')
      })
    }
  }

  if (!user) {
    return <Box sx={{alignItems: "center", textAlign: "center"}}>
      <LoginAlert/>
    </Box>
  }

  return (
    <Stack direction={"column"} spacing={2}>
      {errors && errors.map((itm, key) => {
        return <Alert key={key} severity={"error"}>{itm?.message}</Alert>
      })}
      {messages && messages.map((itm, key) => {
        return <Alert key={key} severity={itm?.type || "info"}>{itm?.text}</Alert>
      })}
      <Stepper activeStep={activeStep}>
        {steps.map((label, index) => (
          <Step key={label} completed={completed[index]}>
            <StepButton color="inherit" onClick={handleStep(index)}>
              {label}
            </StepButton>
          </Step>
        ))}
      </Stepper>
      {activeStep == 0 && <>
        {!showScan && !showTagScan && <div className={styles.scanTextWrapper}>
          <FaHiking size={100}/>
          <div>
            <p>{t("scan_text")}</p>
            <p>{t("scan_text_2")}</p>
          </div>
        </div>}
        {!showScan && !showTagScan && <div className={styles.scanBtnWrapper}>
          <Button sx={{fontSize: 30}} startIcon={<BsQrCodeScan size={100}/>} variant={"contained"}
                  onClick={() => setShowScan(true)}>{t("scan_button")}</Button>
          <Button sx={{fontSize: 30}} startIcon={<PiGpsFix size={100}/>} variant={"contained"}
                  onClick={() => {
                    setShowTagScan(true)
                  }}>{t("tagscan_button")}</Button>
        </div>}
        {showScan && <ScanSegment handleNext={handleNext}/>}
        {showTagScan && <TagScanSegment handleBack={handleBack} handleNext={handleNext}/>}
      </>}
      {activeStep == 1 &&
          <Stack direction={"column"} spacing={2} sx={{alignItems: 'flex-start'}}>
            {!transport && !direction && <>
              <Typography variant={'h5'}>{t("select_direction_transport")}</Typography>
              <Typography variant={'h6'}>{!userTransport ? t('foot') : t(userTransport)}</Typography>
              <Switch value={transport} onChange={() => {
                const value = userTransport === 'foot' ? 'bicycle' : 'foot'
                setUserTransport(value)
                //appStore.setScanningTransport(value)
              }}/>
              <Typography
                  variant={'h6'}>{userDirection === 'normal' ? t('normal_direction') : t('rome_direction')}</Typography>
              <Switch value={direction} onChange={() => {
                const value = userDirection === 'normal' ? 'inverted' : 'normal'
                setUserDirection(value)
                //appStore.setScanningDirection(value)
              }}/>
              <Divider/>
            </>}
            <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} sx={{alignItems: 'center'}}>
              <WalkBtns qrCode={scannedQrCode} handleNext={handleNext}/>
            </Stack>
            {showAbandon &&
                <div style={{whiteSpace: "pre-line"}}>
                  <div dangerouslySetInnerHTML={{__html: t.raw("start_new_path")}}/>
                  <Button sx={{fontSize: 14, marginTop: 10}} startIcon={<RxExit size={20}/>} variant={"contained"}
                          onClick={() => leavePath()}>{t("leave_path")}</Button>
                </div>}
          </Stack>}
      {activeStep == 2 && <div>
        <Card>
          <CardHeader title={'Azione'}/>
          <CardContent>
            <p>{t('Type')}: <strong>{action ? t(action) : ''}</strong></p>
            <p>{t('Path')}: <strong>{path?.name}</strong></p>
            <WalkActions segments={segments} qrCode={scannedQrCode} action={action}/>
            <p>{t('transport')}: <strong>{t((transport || userTransport) ?? 'foot')}</strong></p>
            <p>{t('direction')}: <strong>{(!direction && !userDirection) ? t('normal_direction') : t('rome_direction')}</strong>
            </p>
          </CardContent>
          <CardActions>
            <Stack sx={{justifyContent: "center", justifyItems: "center"}} spacing={2}
                   direction={{xs: 'column', sm: 'row'}}>
              <Fab onClick={() => {
                handleConfirm()
              }} variant="extended" color={"success"}>
                <DoneOutlineIcon sx={{mr: 1}}/>
                {t('OK')}
              </Fab>
              <Fab onClick={() => {
                handleBack()
              }} variant="extended" color={"error"}>
                <ClearIcon sx={{mr: 1}}/>
                {t('CANCEL')}
              </Fab>
            </Stack>
          </CardActions>
        </Card>
      </div>}
      {activeStep == 3 && <div>
        {errors && errors.map((error, key) => {
          if (!error) return null
          return <div key={key}><Alert icon={<ClearIcon fontSize="inherit"/>} severity="error">
            {error.message}
          </Alert></div>
        })
        }
        {showAbandon && <Button sx={{fontSize: 14, marginTop: 10}} startIcon={<RxExit size={20}/>} variant={"contained"}
                                onClick={() => leavePath()}>{t("leave_path")}</Button>}
        {results &&
            <>
              <Alert icon={<DoneOutlineIcon fontSize="inherit"/>} severity="success">
                {results.message}
              </Alert>
              <div>
                {!results.path_ended && <p className={styles.successThankyou}>{t("success_thankyou")}</p>}
                {results.path_ended && <p className={styles.successThankyou}>{t("success_thankyou_end")}</p>}
                <p></p>
                <p className={styles.leaveMessage}>{t("leave_a_message_txt")}</p>
                <div className={styles.finalBtnsWrapper}>
                  <Link href={"/create-share/" + currentSegment}>
                    <Button sx={{fontSize: 14, marginTop: 10}} startIcon={<MdOutlineMobileScreenShare size={20}/>}
                            variant={"contained"}>{t("leave_a_message")}
                    </Button>
                  </Link>
                  <Link href={"/cammino/" + currentPath}>
                    <Button onClick={() => {
                    }} sx={{fontSize: 14}} startIcon={<FaHiking size={20}/>}
                            variant={"contained"}>{t("show_segments")}
                    </Button>
                  </Link>
                  <Link href={"/places"}>
                    <Button onClick={() => {
                    }} sx={{fontSize: 14}} startIcon={<PlaceIcon/>}
                            variant={"contained"}>{t("search_poi")}
                    </Button>
                  </Link>
                </div>
              </div>
            </>
        }
      </div>}

    </Stack>
  )
}
